import { toanQuyen } from '../hooks/usePermission';
import { Suspense } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';

import { Skeleton } from '../components/ui';
import { ForbiddenView } from '../components/ForbiddenView';
import { useAuthStore } from '../stores/auth';
import { TIEN_TO } from '../lib/duong-dan-cong';
import { duocVao, moduleDauTien, timModule } from './modules';
import type { DuongCu, ModuleDef } from './modules';

/** Khung chờ trong lúc chunk của module đang tải. */
function KhungCho() {
  return (
    <div className="flex h-full flex-col gap-4 bg-subtle p-4">
      <Skeleton className="h-20 w-full rounded-lg" />
      <Skeleton className="h-64 w-full rounded-lg" />
    </div>
  );
}

/**
 * Kiểm quyền bản **reactive**, dùng trong cây React.
 *
 * Quyền đổi giữa phiên thì giao diện ẩn/hiện ngay, không phải tải lại trang.
 *
 * `SUPER_ADMIN` vẫn mở mọi màn, nhưng sau ADR-0074 **chỉ tài khoản mẫu còn mang
 * nhãn đó** (`mock/fixtures.ts`): tài khoản đăng nhập thật nhận quyền từ ngữ
 * cảnh do server tính, nên không ai có khoá mở mọi cửa nữa. Bỏ nhánh này đòi
 * cấp quyền tường minh cho tài khoản mẫu ở cả sáu chỗ kiểm quyền
 * (`usePermission`, `PermissionGuard`, `Sidebar`, `useManDauTien`); đó là việc
 * của tầng dữ liệu mẫu, không thuộc ADR-0074 — `TODO(ADR-0074)`.
 */
function useCoQuyen() {
  const user = useAuthStore((s) => s.user);
  return (ma: string): boolean => {
    if (!user) return false;
    if (toanQuyen(user)) return true;
    return user.permissions.includes(ma);
  };
}

/**
 * Bọc một module của registry: kiểm quyền rồi dựng, kèm khung chờ khi lazy.
 *
 * Phạm vi cổng do `PhamViGuard` ở `MainLayout` chặn trước khi tới đây, nên chỗ
 * này chỉ còn lo quyền. Gọi `duocVao` không kèm phạm vi là **có chủ đích**:
 * chặn hai lần sẽ cho ra hai câu trả lời khác nhau cho cùng một tình huống.
 */
export function RouteModule({ module, con = false }: { module: ModuleDef; con?: boolean }) {
  const duoc = useCoQuyen();
  if (!duocVao(module, duoc)) return <ForbiddenView />;

  const Component = con && module.ComponentCon ? module.ComponentCon : module.Component;
  return (
    <Suspense fallback={<KhungCho />}>
      <Component />
    </Suspense>
  );
}

/**
 * `/admin` trần: đẩy về module đầu tiên **người này** được vào.
 *
 * Không cố định mục đầu danh sách — xem ghi chú ở `moduleDauTien`: người chỉ
 * có quyền nhật ký mà bị đẩy về `Tenant` thì lại bị đẩy tiếp, thành vòng
 * chuyển hướng vô tận.
 */
export function VeModuleDauTien() {
  const duoc = useCoQuyen();
  const m = moduleDauTien(duoc, 'nen-tang');
  if (!m) return <ForbiddenView />;
  return <Navigate to={`${TIEN_TO['nen-tang']}/${m.id}`} replace />;
}

/**
 * Route `/:moduleId` — tra registry theo tham số đường dẫn rồi dựng.
 *
 * ⚠️ Chỉ dùng được khi route khai bằng **đoạn động** `/:moduleId`. Với route
 * khai bằng đường dẫn tường minh (`/tenant`) thì `useParams()` không có gì,
 * `timModule('')` trả `undefined`, và màn hình lặng lẽ bị đẩy về `/` — đúng
 * triệu chứng "bấm vào menu mà không chuyển trang". Trường hợp đó dùng thẳng
 * `<RouteModule module={m} />`.
 */
export function ManHinhTheoRegistry() {
  const { moduleId = '' } = useParams<{ moduleId: string }>();
  const m = timModule(moduleId);
  if (!m) return <Navigate to={TIEN_TO['nen-tang']} replace />;
  return <RouteModule module={m} />;
}

/**
 * Đường dẫn cũ của một màn đã gộp hoặc đổi tên (`DUONG_CU` ở registry): chuyển sang màn đích, ghép `thamSo` của
 * dòng khai lên query đang có. `replace` để nút Quay lại không dội về đường cũ rồi bị chuyển tiếp lần nữa.
 */
export function ChuyenDuongCu({ duong }: { duong: DuongCu }) {
  const { search, hash } = useLocation();
  const q = new URLSearchParams(search);
  for (const [k, v] of Object.entries(duong.thamSo ?? {})) q.set(k, v);
  const chuoi = q.toString();
  return <Navigate to={`${TIEN_TO['nen-tang']}/${duong.den}${chuoi ? `?${chuoi}` : ''}${hash}`} replace />;
}
